<template>
    <component
        :is="isLink ? 'Link' : 'button'"
        :href="result.url"
        as="a"
        @keydown="handleLinkKeyDown"
        :disabled="disabled"
        @click.stop="$emit('resultClicked', result)"
        :class="[
            { 'cursor-pointer': !isLink && !disabled },
            { 'flex w-full': !isLink },
            { 'cursor-not-allowed': disabled },
            'group focus:outline-none focus:ring-0 border-border',
        ]"
        tabindex="0"
    >
        <div
            :class="[
                { 'my-3': !small },
                'max-w-full w-full overflow-hidden flex gap-x-1 pl-3 group-hover:bg-gray-50 group-focus:bg-primary group-focus:text-primary-text transition-colors duration-300',
            ]"
        >
            <div class="flex flex-col justify-center">
                <ModelImage
                    :src="result.image_url"
                    :modelClass="result.modelClass"
                    :typeId="result.type_id"
                    :fileUrl="result.file"
                    imageClasses="object-cover w-full h-full"
                    :class="[
                        result.modelClass == 'user'
                            ? 'rounded-full'
                            : 'rounded-md',
                        'aspect-square',
                        { 'opacity-50': disabled },
                    ]"
                    :heightClasses="small ? 'h-9' : imageHeight"
                />
            </div>
            <div
                :class="[
                    small ? 'py-3 flex flex-col justify-center' : 'py-7',
                    'flex-shrink px-3 min-w-0 flex-grow text-left',
                ]"
            >
                <div
                    :class="[
                        { 'opacity-50': disabled },
                        'flex justify-between items-center gap-x-3',
                    ]"
                >
                    <p
                        :class="[
                            small ? 'text-base' : 'text-xl mb-1.5',
                            'font-header [&_mark]:bg-primary [&_mark]:text-primary-text [&_mark]:underline [&_mark]:underline-offset-2',
                        ]"
                        v-html="formatHighlightedText(result.name_highlight)"
                    ></p>
                    <ModelClassTag
                        :modelClass="result.modelClass"
                        class="flex-shrink-0"
                    />
                </div>
                <p
                    v-if="disabled"
                    v-text="disabledMessage"
                    class="italic font-bold"
                />
                <div v-else-if="showDescription">
                    <div class="pt-1 pb-2 flex flex-wrap gap-x-2">
                        <TypeTag
                            :typeName="result.type_name"
                            v-if="result.type_name"
                        />
                        <PartnerTag v-if="result.is_partner" />
                    </div>

                    <p
                        v-if="displayShortDescription"
                        :class="[
                            'text-sm leading-[1.1rem] [&_mark]:bg-primary [&_mark]:text-primary-text [&_mark]:underline [&_mark]:underline-offset-2',
                            lineClamp,
                        ]"
                        v-html="
                            formatHighlightedText(
                                result.short_description_highlight
                            )
                        "
                    />
                    <p
                        v-else-if="result.description_highlight"
                        :class="[
                            'text-sm leading-[1.1rem] [&_mark]:bg-primary [&_mark]:text-primary-text [&_mark]:underline [&_mark]:underline-offset-2',
                            lineClamp,
                        ]"
                        v-html="
                            formatHighlightedText(result.description_highlight)
                        "
                    />
                </div>
            </div>
        </div>
    </component>
</template>

<script>
import { Link } from '@inertiajs/vue3'
import ModelImage from '@/Components/ModelComponents/ModelImage.vue'
import ModelClassTag from '@/Components/ModelComponents/ModelClassTag.vue'
import TypeTag from '@/Components/Elements/Tags/TypeTag.vue'
import PartnerTag from '@/Components/Elements/Tags/PartnerTag.vue'

export default {
    components: {
        Link,
        ModelImage,
        ModelClassTag,
        TypeTag,
        PartnerTag,
    },

    props: {
        isLink: {
            type: Boolean,
            default: true,
        },
        result: {
            type: Object,
            required: true,
        },
        lineClamp: {
            type: String,
            default: 'line-clamp-4',
        },
        imageHeight: {
            type: String,
            default: 'h-40',
        },
        small: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        disabledMessage: {
            type: String,
            default: '',
        },
    },

    methods: {
        formatHighlightedText(text) {
            return text ? text.replaceAll('</mark> <mark>', ' ') : ''
        },

        handleLinkKeyDown(event) {
            if (event.code === 'Enter' || event.code === 'Space') {
                event.target.click()
                event.preventDefault()
            }
        },
    },

    computed: {
        /**
         * Returns true if the short description should be displayed instead of the description
         * This is decided on if the short description is not empty, or that the description has some highlight (if it contains <mark></mark>) and the short description does not have it
         */
        displayShortDescription() {
            return (
                this.result.short_description_highlight &&
                this.result.short_description_highlight.length > 0 &&
                (this.result.short_description_highlight.includes('<mark>') ||
                    !this.result.description_highlight.includes('</mark>'))
            )
        },

        showDescription() {
            return (
                !this.small &&
                (this.displayShortDescription ||
                    this.result.description_highlight)
            )
        },
    },
}
</script>
